import microsoftLogo from "@/assets/ms-icons/microsoft.svg";
import TrackedLink from "@/components/TrackedLink";
import { Button } from "@/components/ui/button";
import {
  HardDrive,
  CloudUpload,
  Shield,
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  Laptop,
  
  FolderSync,
  FileWarning,
  LockKeyhole,
  Users,
  Clock,
  Zap,
  HeartHandshake,
  Settings,
  ImageIcon,
  Download,
  FileText,
  LayoutDashboard,
  RefreshCw,
} from "lucide-react";
import { motion } from "framer-motion";
import managedHeroBg from "@/assets/managed-hero-bg.jpg";
import oneDriveIcon from "@/assets/ms-icons/onedrive.svg";
import appleIcon from "@/assets/ms-icons/apple.svg";

const scenarios = [
  {
    icon: LayoutDashboard,
    title: "Alles op het bureaublad",
    description:
      "Je medewerker slaat alles op z'n bureaublad op. Laptop stuk? Alles weg. Geen backup, geen versiebeheer, geen manier om het terug te halen.",
  },
  {
    icon: Laptop,
    title: "Laptop gestolen",
    description:
      "Een medewerker laat z'n laptop in de trein liggen. Alle lokale bestanden — offertes, contracten, klantgegevens — zijn verdwenen. En mogelijk in verkeerde handen.",
  },
  {
    icon: LockKeyhole,
    title: "Ransomware versleutelt alles",
    description:
      "Een medewerker opent een bijlage. Ransomware versleutelt de hele lokale schijf. Zonder cloud-backup ben je alles kwijt — of je betaalt losgeld.",
  },
  {
    icon: FileWarning,
    title: "Niet bij je bestanden",
    description:
      "Een medewerker werkt thuis op z'n eigen laptop, maar de bestanden staan op de kantoor-PC. Geen toegang, geen productiviteit, frustratie alom.",
  },
];

const comparison = {
  without: [
    "Geen automatische back-up van lokale bestanden",
    "Geen versiegeschiedenis — verkeerd opgeslagen = kwijt",
    "Geen bescherming tegen ransomware",
    "Bestanden weg bij verlies, diefstal of defect",
    "Niet beschikbaar op andere apparaten",
    "Geen mogelijkheid tot samenwerking",
  ],
  with: [
    "Automatische back-up + extra retentie door Rinqel",
    "Versiegeschiedenis voor alle bestanden — altijd terug te draaien",
    "Geavanceerde ransomware-bescherming via Microsoft 365",
    "Naadloze apparaatwissel — direct verder werken",
    "Altijd en overal toegankelijk",
    "Veilige samenwerking en delen met collega's",
  ],
};

const voordelen = [
  {
    icon: Shield,
    title: "Nooit meer bestanden kwijt",
    description:
      "Alles wordt automatisch geback-upt naar de cloud. Laptop kapot, gestolen of verloren? Je bestanden staan er gewoon nog.",
  },
  {
    icon: RefreshCw,
    title: "Geen paniek bij problemen",
    description:
      "Laptop stuk? Binnen een half uur draait je medewerker weer op een nieuwe — met alle bestanden intact. Rinqel regelt het.",
  },
  {
    icon: Zap,
    title: "Overal doorwerken",
    description:
      "Thuis, op kantoor, in de trein of op een andere laptop: je bestanden zijn altijd up-to-date en direct beschikbaar.",
  },
  {
    icon: ShieldCheck,
    title: "Beschermd tegen ransomware",
    description:
      "Wordt een bestand versleuteld door ransomware? Je draait het terug naar een eerdere versie. Zonder losgeld, zonder dataverlies.",
  },
  {
    icon: HeartHandshake,
    title: "Medewerkers merken er niks van",
    description:
      "Geen extra stappen, geen gedoe. Medewerkers slaan bestanden op zoals ze gewend zijn — de rest gebeurt automatisch.",
  },
  {
    icon: Users,
    title: "Eén systeem, nul gedoe",
    description:
      "Elke werkplek werkt hetzelfde. Geen uitzonderingen, geen vergeten laptops, geen losse afspraken. Het werkt gewoon.",
  },
];

const windowsFolders = [
  { icon: LayoutDashboard, name: "Bureaublad", synced: true },
  { icon: FileText, name: "Documenten", synced: true },
  { icon: Download, name: "Downloads", synced: true },
  { icon: ImageIcon, name: "Afbeeldingen", synced: true },
];

const macFolders = [
  { icon: LayoutDashboard, name: "Bureaublad", synced: true },
  { icon: FileText, name: "Documenten", synced: true },
  { icon: Download, name: "Downloads", synced: false },
  { icon: ImageIcon, name: "Afbeeldingen", synced: false, note: "Foto's blijven in iCloud — dat werkt beter op een Mac" },
];

const PraktijkOneDrive = () => {
  return (
    <>
      {/* Hero */}
      <section className="relative min-h-[420px] overflow-hidden bg-black text-primary-foreground">
        <img loading="lazy" decoding="async"
          src={managedHeroBg}
          alt="Moderne werkplek met cloud-opslag"
          className="absolute inset-0 h-full w-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="container relative z-10 py-20 md:py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-sm"
            >
              <img loading="lazy" decoding="async" src={oneDriveIcon} alt="OneDrive" className="h-7 w-7" />
            </motion.div>
            <span className="mb-4 inline-block rounded-full bg-primary/20 px-4 py-1.5 text-sm font-semibold text-primary">
              Praktijkvoorbeeld
            </span>
            <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
              Waar slaan jouw medewerkers hun{" "}
              <span className="text-primary">bestanden</span> op?
            </h1>
            <p className="mb-8 text-lg text-primary-foreground/80 md:text-xl">
              Veel medewerkers slaan bestanden op vertrouwde plekken zoals
              Bureaublad of Documenten. Maar worden die mappen ook geback-upt?
              Met OneDrive en Rinqel-beheer staat alles automatisch veilig in de
              cloud — zonder extra handelingen.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {[
                "Automatische back-up",
                "Ransomware-bescherming",
                "Windows & macOS",
                "Altijd toegankelijk",
              ].map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-2 text-sm text-primary-foreground/90"
                >
                  <CheckCircle2 className="h-4 w-4 text-rinqel-green" />
                  <span>{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Herkenning */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Herken je dit?
            </h2>
            <p className="mb-12 text-lg text-muted-foreground">
              Dit zijn situaties die bij vrijwel elk bedrijf voorkomen. En ze
              kosten meer dan je denkt.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {scenarios.map((scenario) => (
              <div
                key={scenario.title}
                className="group rounded-2xl border border-border bg-card p-6 transition-all hover:border-destructive/30 hover:shadow-lg"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
                  <scenario.icon className="h-6 w-6 text-destructive" />
                </div>
                <h3 className="mb-2 text-lg font-bold">{scenario.title}</h3>
                <p className="text-muted-foreground">{scenario.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* De oplossing */}
      <section className="bg-rinqel-green/5 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-8 flex h-16 w-16 items-center justify-center rounded-2xl bg-rinqel-green/10">
              <CloudUpload className="h-8 w-8 text-rinqel-green" />
            </div>
            <h2 className="mb-6 text-3xl font-bold md:text-4xl">
              OneDrive: automatisch veilig opslaan
            </h2>
            <div className="space-y-4 text-lg text-muted-foreground">
              <p>
                Bij Rinqel configureren we de werkplekken zó dat de
                belangrijkste mappen — Bureaublad, Documenten en meer —{" "}
                <strong>automatisch synchroniseren naar OneDrive</strong>.
                Medewerkers merken er niks van: ze slaan bestanden op zoals ze
                gewend zijn.
              </p>
              <p>
                Maar achter de schermen worden alle bestanden{" "}
                <strong>veilig geback-upt</strong>, voorzien van{" "}
                <strong>versiegeschiedenis</strong> en beschermd tegen{" "}
                <strong>ransomware</strong>. Bij verlies, diefstal of een
                defecte laptop zet je binnen minuten een nieuwe werkplek op —
                met alle bestanden intact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Windows vs macOS */}
      <section className="py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <div className="mb-4 flex justify-center">
              <img loading="lazy" decoding="async" src={oneDriveIcon} alt="OneDrive" className="h-10 w-10" />
            </div>
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Werkt op Windows én macOS
            </h2>
            <p className="mb-12 text-lg text-muted-foreground">
              Wij regelen de configuratie, jij merkt er niks van. Op beide platformen synchroniseren de belangrijkste mappen automatisch naar OneDrive.
            </p>
          </div>

          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
            {/* Windows */}
            <div className="rounded-2xl border border-primary/20 bg-card p-8">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-rinqel-green/10">
                  <img loading="lazy" decoding="async" src={microsoftLogo} alt="Microsoft" className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Windows</h3>
                  <p className="text-sm text-muted-foreground">
                    Alles gesynchroniseerd
                  </p>
                </div>
              </div>
              <ul className="space-y-3">
                {windowsFolders.map((folder) => (
                  <li key={folder.name} className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded bg-rinqel-green/10">
                      <FolderSync className="h-3.5 w-3.5 text-rinqel-green" />
                    </div>
                    <span className="font-medium">{folder.name}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-xl bg-rinqel-green/5 p-4">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Resultaat:</strong> Alles
                  wat medewerkers opslaan is automatisch veilig, centraal en hersteldbaar.
                </p>
              </div>
            </div>

            {/* macOS */}
            <div className="rounded-2xl border border-border bg-card p-8">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-foreground/5">
                  <img loading="lazy" decoding="async"
                    src={appleIcon}
                    alt="Apple"
                    className="h-5 w-auto object-contain"
                  />
                </div>
                <div>
                  <h3 className="text-xl font-bold">macOS</h3>
                  <p className="text-sm text-muted-foreground">
                    Slim gesynchroniseerd
                  </p>
                </div>
              </div>
              <ul className="space-y-3">
                {macFolders.map((folder) => (
                  <li key={folder.name} className="flex items-start gap-3">
                    <div
                      className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded ${
                        folder.synced
                          ? "bg-rinqel-green/10"
                          : "bg-muted"
                      }`}
                    >
                      {folder.synced ? (
                        <FolderSync className="h-3.5 w-3.5 text-rinqel-green" />
                      ) : (
                        <folder.icon className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                    </div>
                    <div>
                      <span className="font-medium">
                        {folder.name}
                        {!folder.synced && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (niet via OneDrive)
                          </span>
                        )}
                      </span>
                      {folder.note && (
                        <p className="text-xs text-muted-foreground">
                          {folder.note}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-xl bg-muted/50 p-4">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Slim ingedeeld:</strong> Foto's
                  blijven in iCloud — precies zoals Apple het bedoeld heeft. Geen dubbele
                  opslag, geen gedoe.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Voor/na vergelijking */}
      <section className="border-y border-border bg-muted/30 py-16 md:py-24">
        <div className="container">
          <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl">
            Het verschil
          </h2>
          <div className="mx-auto grid max-w-5xl gap-8 md:grid-cols-2">
            {/* Zonder */}
            <div className="rounded-2xl border border-destructive/20 bg-card p-8">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
                  <XCircle className="h-5 w-5 text-destructive" />
                </div>
                <h3 className="text-xl font-bold text-destructive">
                  Zonder OneDrive-beheer
                </h3>
              </div>
              <ul className="space-y-4">
                {comparison.without.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive/60" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Met */}
            <div className="rounded-2xl border border-rinqel-green/30 bg-card p-8 shadow-lg shadow-rinqel-green/5">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rinqel-green/10">
                  <CheckCircle2 className="h-5 w-5 text-rinqel-green" />
                </div>
                <h3 className="text-xl font-bold text-rinqel-green">
                  Met OneDrive via Rinqel
                </h3>
              </div>
              <ul className="space-y-4">
                {comparison.with.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-rinqel-green" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-xl bg-rinqel-green/5 p-4">
                <p className="text-sm font-semibold text-rinqel-green">
                  Kortom: je medewerkers werken gewoon door. De rest regelen wij.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Voordelen */}
      <section className="py-16 md:py-24">
        <div className="container">
          <h2 className="mb-12 text-center text-3xl font-bold md:text-4xl">
            Wat levert het je op?
          </h2>
          <div className="mx-auto grid max-w-5xl gap-6 md:grid-cols-2 lg:grid-cols-3">
            {voordelen.map((voordeel) => (
              <div
                key={voordeel.title}
                className="rounded-2xl border border-rinqel-green/20 bg-card p-6 transition-all hover:border-rinqel-green/40 hover:shadow-lg hover:shadow-rinqel-green/5"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-rinqel-green/10">
                  <voordeel.icon className="h-6 w-6 text-rinqel-green" />
                </div>
                <h3 className="mb-2 text-lg font-bold">{voordeel.title}</h3>
                <p className="text-sm text-muted-foreground">
                  {voordeel.description}
                </p>
              </div>
            ))}
          </div>

          {/* Managed Werkplek link */}
          <div className="mx-auto mt-12 max-w-3xl">
            <div className="rounded-xl border border-rinqel-green/30 bg-rinqel-green/5 p-5 flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-rinqel-green/10">
                <ShieldCheck className="h-6 w-6 text-rinqel-green" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">
                  Dit is standaard inbegrepen in onze Managed Werkplek.
                </p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  OneDrive-configuratie, back-up, monitoring en
                  ransomware-bescherming — allemaal automatisch geregeld.
                </p>
                <TrackedLink
                  to="/diensten/werkplek/managed"
                  trackLabel="Managed Werkplek"
                  trackLocation="praktijk_onedrive_voordelen"
                  className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-rinqel-green hover:underline"
                >
                  Bekijk de Managed Werkplek →
                </TrackedLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conclusie */}
      <section className="border-t border-border bg-muted/30 py-12 md:py-16">
        <div className="container">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-xl font-semibold md:text-2xl">
              Het maakt niet uit waar je medewerkers denken dat ze opslaan.{" "}
              <span className="text-primary">
                Wij zorgen dat het altijd veilig en centraal staat — automatisch.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* Extra uitleg */}
      <section className="py-12 md:py-16">
        <div className="container">
          <div className="mx-auto max-w-3xl space-y-6 text-muted-foreground">
            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-foreground">Wat we precies doen</h2>
              <p>
                We zetten OneDrive aan voor elke medewerker en verhuizen de bestaande Documenten-,
                Bureaublad- en Afbeeldingenmappen automatisch naar de cloud. De medewerker merkt
                er niets van: alle bestanden staan na de overstap op precies dezelfde plek, alleen
                nu ook veilig in de cloud.
              </p>
              <p>
                Daarna stellen we versiebeheer, prullenbak-bewaring en back-upbeleid in. Bij
                ransomware of een per ongeluk verwijderd bestand kunnen we tot 30 dagen terug —
                of langer, als je dat wilt.
              </p>
            </div>

            <div className="space-y-3">
              <h2 className="text-2xl font-bold text-foreground">Wat het oplevert</h2>
              <p>
                Geen dataverlies meer bij een kapotte laptop, een gestolen telefoon of een
                gijzelvirus. Een nieuwe medewerker is binnen een uur productief: laptop aan,
                inloggen, alle bestanden staan er. Stopt iemand met werken? Dan houd jij alle
                bestanden, in plaats van dat ze met de medewerker verdwijnen.
              </p>
              <p>
                Bovendien werk je voortaan op elk apparaat aan dezelfde, actuele bestanden — op
                kantoor, thuis of onderweg. Zonder USB-sticks, mailtjes naar jezelf of
                "welke versie was de laatste?"-vragen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-gradient-to-br from-primary/5 via-rinqel-blue/5 to-rinqel-green/5 py-16 md:py-24">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="mb-4 text-3xl font-bold md:text-4xl">
              Nooit meer zorgen over dataverlies?
            </h2>
            <p className="mb-8 text-lg text-muted-foreground">
              Wij configureren OneDrive voor je hele organisatie. Automatische
              back-up, ransomware-bescherming en altijd toegang tot je
              bestanden — zonder gedoe.
            </p>
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <Button variant="hero" size="lg" asChild>
                <TrackedLink
                  to="/configurator"
                  trackLabel="Bereken je prijs"
                  trackLocation="praktijk_onedrive_cta"
                >
                  Bereken je prijs
                </TrackedLink>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <TrackedLink
                  to="/contact"
                  trackLabel="Neem contact op"
                  trackLocation="praktijk_onedrive_cta"
                >
                  Neem contact op
                </TrackedLink>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PraktijkOneDrive;
